import {
  AllButtonVariants,
  ButtonSize,
  ButtonSizeConfig,
  ButtonVariant,
} from "@/modules/common/components/ui-kit/buttons/shared/types";
import { ButtonProps, SxProps } from "@mui/material";
import { nawyTheme } from "@/styles/nawy-theme/nawy-theme";

// ----------------------- MUI Button Variant Mapping ---------------- //
export const buttonVariantsMap = new Map<ButtonVariant, ButtonProps["variant"]>(
  [
    ["primary", "contained"],
    ["elevated", "contained"],
    ["tonal", "contained"],
    ["negative", "contained"],
    ["outlined-secondary", "outlined"],
    ["outlined-primary", "outlined"],
    ["text", "text"],
  ]
);

// ----------------------- Button Variants Styles ---------------- //
export const primaryStyles = {
  background: nawyTheme.nawy.colors.semantic.layer.accent,
  color: nawyTheme.nawy.colors.semantic.text.onColor,
  boxShadow: "none",
  "&:hover": {
    background: nawyTheme.nawy.colors.semantic.state.light08,
    color: nawyTheme.nawy.colors.semantic.text.accent,
  },
  "&:active": {
    background: nawyTheme.nawy.colors.semantic.state.light16,
  },
  "&:disabled": {
    background: nawyTheme.nawy.colors.semantic.layer.extension.disabled,
  },
};

export const outlinedPrimaryStyles = {
  background: nawyTheme.nawy.colors.semantic.layer.primary,
  color: nawyTheme.nawy.colors.semantic.text.accent,
  ...nawyTheme.nawy.typography.english.variants.body.md.semibold,
  border: `1px solid ${nawyTheme.nawy.colors.semantic.border.accent}`,
  boxShadow: "none",
  "&:hover": {
    background: nawyTheme.nawy.colors.semantic.state.onColor08,
  },
  "&:active": {
    background: nawyTheme.nawy.colors.semantic.state.onColor12,
  },
  "&:disabled": {
    border: nawyTheme.nawy.colors.semantic.layer.extension.disabled,
    color: nawyTheme.nawy.colors.semantic.text.extension.disabled,
  },
};

export const outlinedSecondaryStyles = {
  background: nawyTheme.nawy.colors.semantic.layer.primary,
  color: nawyTheme.nawy.colors.semantic.text.primary,
  border: `1px solid ${nawyTheme.nawy.colors.semantic.border.primary}`,
  boxShadow: "none",
  "&:hover": {
    background: nawyTheme.nawy.colors.semantic.state.light08,
  },
  "&:active": {
    background: nawyTheme.nawy.colors.semantic.state.onDark12,
  },
  "&:disabled": {
    background: nawyTheme.nawy.colors.semantic.layer.extension.disabledLight,
    color: nawyTheme.nawy.colors.semantic.text.tertiary,
  },
};

export const ghostStyles = {
  background: "transparent",
  color: nawyTheme.nawy.colors.semantic.text.accent,
  boxShadow: "none",
  "&:hover": {
    background: nawyTheme.nawy.colors.semantic.state.onColor08,
  },
  "&:active": {
    background: nawyTheme.nawy.colors.semantic.state.onColor12,
  },
  "&:disabled": {
    color: nawyTheme.nawy.colors.semantic.text.extension.disabled,
  },
};

export const textStyles = {
  background: "transparent",
  color: nawyTheme.nawy.colors.semantic.text.accent,
  boxShadow: "none",
  ...nawyTheme.nawy.typography.english.variants.body.md.semibold,
  "&:hover": {
    background: nawyTheme.nawy.colors.semantic.state.onColor08,
  },
  "&:active": {
    background: nawyTheme.nawy.colors.semantic.state.onColor12,
  },
  "&:disabled": {
    color: nawyTheme.nawy.colors.semantic.text.extension.disabled,
  },
};

export const elevatedStyles = {
  background: nawyTheme.nawy.colors.semantic.layer.primary,
  color: nawyTheme.nawy.colors.semantic.text.primary,
  boxShadow: nawyTheme.nawy.elevation.sm,
  "&:hover": {
    background: nawyTheme.nawy.colors.semantic.state.onDark08,
  },
  "&:active": {
    background: nawyTheme.nawy.colors.semantic.state.onDark12,
  },
  "&:disabled": {
    background: nawyTheme.nawy.colors.semantic.layer.extension.disabledLight,
    color: nawyTheme.nawy.colors.semantic.text.tertiary,
  },
};

export const tonalStyles = {
  background: nawyTheme.nawy.colors.semantic.layer.accentLight,
  color: nawyTheme.nawy.colors.semantic.text.accent,
  boxShadow: "none",
  "&:hover": {
    background: nawyTheme.nawy.colors.semantic.state.onColor08,
  },
  "&:active": {
    background: nawyTheme.nawy.colors.semantic.state.onColor12,
  },
  "&:disabled": {
    background: nawyTheme.nawy.colors.semantic.layer.extension.disabledLight,
    color: nawyTheme.nawy.colors.semantic.text.tertiary,
  },
};

export const negativeStyles = {
  background: nawyTheme.nawy.colors.semantic.layer.extension.negative,
  color: nawyTheme.nawy.colors.semantic.text.onColor,
  boxShadow: "none",
  "&:hover": {
    background: nawyTheme.nawy.colors.semantic.state.onColor08,
  },
  "&:active": {
    background: nawyTheme.nawy.colors.semantic.state.onColor12,
  },
  "&:disabled": {
    background: nawyTheme.nawy.colors.semantic.layer.extension.disabled,
    color: nawyTheme.nawy.colors.semantic.text.onColor,
  },
};

export const variantStyles = new Map<AllButtonVariants, SxProps>([
  ["primary", primaryStyles],
  ["elevated", elevatedStyles],
  ["tonal", tonalStyles],
  ["negative", negativeStyles],
  ["ghost", ghostStyles],
  ["text", textStyles],
  ["outlined-primary", outlinedPrimaryStyles],
  ["outlined-secondary", outlinedSecondaryStyles],
]);

// ----------------------- Button Size Configurations ---------------- //
export const smButtonSize: ButtonSizeConfig = {
  height: "32px",
  padding: `${nawyTheme.nawy.spacing.xs} ${nawyTheme.nawy.spacing.sm}`,
  fontSize: "14px",
};

export const mdButtonSize: ButtonSizeConfig = {
  height: "36px",
  padding: `${nawyTheme.nawy.spacing.sm} ${nawyTheme.nawy.spacing.md}`,
  fontSize: "14px",
};

export const lgButtonSize: ButtonSizeConfig = {
  height: "40px",
  padding: `${nawyTheme.nawy.spacing.sm} ${nawyTheme.nawy.spacing.lg}`,
  fontSize: "16px",
};

// Size configurations for buttons
export const buttonSizes = new Map<ButtonSize, ButtonSizeConfig>([
  ["sm", smButtonSize],
  ["md", mdButtonSize],
  ["lg", lgButtonSize],
]);

// Size configurations for icon buttons
export const iconButtonSizes = new Map<ButtonSize, string>([
  ["sm", "32px"],
  ["md", "36px"],
  ["lg", "40px"],
]);
